/* eslint-disable */
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../contexts/AuthContext";
import VirtualizedDropdown from "./VirtualizedDropdown/VirtualizedDropdown";
import TextInputField from "./Fields/TextInputField";
import { apiClient } from "../auth/apiClient";
import { Save, Star } from "lucide-react";

export default function SavedViewsManager({
  type,
  currentFilters,
  onApplyFilters,
  onClearFilters,
  compact = false,
  allowLocalFallback = false,
}) {
  const { accessToken, currentUser } = useAuth();

  const [views, setViews] = useState([]);
  const [selectedView, setSelectedView] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [viewName, setViewName] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [confirmOverride, setConfirmOverride] = useState(false);
  const [editingViewId, setEditingViewId] = useState(null);
  const [editingViewName, setEditingViewName] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [viewToDelete, setViewToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savedOpen, setSavedOpen] = useState(false);
  const [storageNotice, setStorageNotice] = useState("");
  const localViewsKey = `mg_saved_views:${currentUser?.id || currentUser?.email || "local"}:${type}`;
  const compactRef = useRef(null);
  useEffect(() => {
    if (!compact || !savedOpen) return undefined;
    const closeOutside = (event) => {
      if (!compactRef.current?.contains(event.target) && !event.target.closest?.(".rs__menu")) setSavedOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [compact, savedOpen]);
  const dropdownRef = useRef(null);
  const hasLoadedInitialView = useRef(false);

  useEffect(() => {
    if (accessToken) {
      hasLoadedInitialView.current = false;
      setSelectedView(null); // Reset selected view when type changes
      if (onClearFilters) {
        onClearFilters({ initial: true }); // Clear filters when switching tabs
      }
      fetchViews();
    }
  }, [type, accessToken, currentUser?.id]);

  const readLocalViews = () => {
    if (!allowLocalFallback) return [];
    try {
      const stored = JSON.parse(localStorage.getItem(localViewsKey) || "[]");
      return Array.isArray(stored) ? stored.filter((view) => view.type === type) : [];
    } catch {
      return [];
    }
  };

  const writeLocalViews = (nextViews) => {
    const typedViews = nextViews.filter((view) => view.type === type);
    localStorage.setItem(localViewsKey, JSON.stringify(typedViews));
    setViews([...typedViews.filter((view) => !view.deleted), ...views.filter((view) => !view.id.startsWith("local:") && !typedViews.some((local) => local.name === view.name))]);
    return typedViews;
  };

  const saveLocalView = (name) => {
    if (!allowLocalFallback) throw new Error("Local Saved Views are disabled.");
    const localViews = readLocalViews();
    const existingLocal = localViews.find((view) => view.name === name);
    const now = new Date().toISOString();
    const savedView = {
      id: existingLocal?.id || `local:${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`}`,
      user_id: currentUser?.id || "local",
      type,
      name,
      filter: currentFilters,
      is_default: isDefault,
      created_at: existingLocal?.created_at || now,
    };
    const nextViews = [...localViews.filter((view) => view.name !== name).map((view) => ({ ...view, is_default: isDefault ? false : view.is_default })), savedView];
    writeLocalViews(nextViews);
    setSelectedView(savedView);
    setStorageNotice("Saved on this browser. Server storage is unavailable.");
    if (onApplyFilters) onApplyFilters(savedView.filter);
    setShowModal(false);
    setConfirmOverride(false);
    setViewName("");
  };

  const fetchViews = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get("/views");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!Array.isArray(data)) return;
      const localViews = readLocalViews();
      const remoteViews = data.filter((view) => view.type === type);
      setViews([...localViews.filter((view) => !view.deleted), ...remoteViews.filter((remote) => !localViews.some((local) => local.name === remote.name))]);
      setStorageNotice(localViews.some((view) => !view.deleted) ? "Some saved views are stored only in this browser." : "");
    } catch (err) {
      console.error("Error fetching views:", err);
      setViews(readLocalViews().filter((view) => !view.deleted));
      setStorageNotice(allowLocalFallback ? "Server storage is unavailable; TikTok views are saved in this browser." : "Could not load saved views.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (views.length > 0 && !hasLoadedInitialView.current) {
      const defaultView = views.find((v) => v.is_default && v.type === type);
      if (defaultView) {
        setSelectedView(defaultView);
        if (onApplyFilters && defaultView.filter) {
          onApplyFilters(defaultView.filter, { initial: true });
        }
      }
      hasLoadedInitialView.current = true;
    }
  }, [views, type, onApplyFilters]);

  const handleToggleDefault = async (viewId) => {
    try {
      const viewToUpdate = views.find((v) => v.id === viewId);
      if (!viewToUpdate) return;
      const newDefaultState = !viewToUpdate.is_default;

      if (viewId.startsWith("local:")) {
        writeLocalViews(readLocalViews().map((view) => ({ ...view, is_default: view.id === viewId ? newDefaultState : newDefaultState ? false : view.is_default })));
        return;
      }

      if (newDefaultState) {
        const defaultViews = views.filter(
          (v) => v.type === type && v.is_default
        );
        for (const defaultView of defaultViews) {
          await apiClient.put(`/views/${defaultView.id}`, {
            ...defaultView,
            is_default: false,
          });
        }
      }

      const response = await apiClient.put(`/views/${viewId}`, {
        ...viewToUpdate,
        is_default: newDefaultState,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      await fetchViews();
    } catch (err) {
      console.error("Error toggling default:", err);
      if (allowLocalFallback) {
        const view = views.find((item) => item.id === viewId);
        if (view) {
          const localViews = readLocalViews();
          const localCopy = { ...view, id: `local:${Date.now()}`, is_default: !view.is_default };
          writeLocalViews([...localViews.filter((item) => item.name !== view.name).map((item) => ({ ...item, is_default: localCopy.is_default ? false : item.is_default })), localCopy]);
        }
      }
    }
  };

  const handleSelectView = (opt) => {
    if (!opt) {
      setSelectedView(null);
      if (onClearFilters) onClearFilters();
      return;
    }
    const view = views.find((v) => v.id === opt.value);
    setSelectedView(view);
    if (view && view.filter && onApplyFilters) {
      onApplyFilters(view.filter);
    }
  };

  const openSaveModal = () => {
    if (dropdownRef.current) dropdownRef.current.blur?.();
    setViewName(selectedView ? selectedView.name : "");
    setIsDefault(selectedView?.is_default || false);
    setConfirmOverride(false);
    setTimeout(() => setShowModal(true), 50);
  };

  const handleSave = async () => {
    let finalViewName =
      viewName ||
      (() => {
        let index = 1,
          generated;
        do {
          generated = `New View ${index++}`;
        } while (views.some((v) => v.name === generated));
        return generated;
      })();

    const existing = views.find((v) => v.name === finalViewName);
    if (existing && !confirmOverride) {
      setConfirmOverride(true);
      return;
    }

    if (existing?.id.startsWith("local:")) {
      saveLocalView(finalViewName);
      return;
    }

    try {
      if (isDefault) {
        const defaultViews = views.filter(
          (v) => v.type === type && v.is_default
        );
        for (const defaultView of defaultViews) {
          await apiClient.put(`/views/${defaultView.id}`, {
            ...defaultView,
            is_default: false,
          });
        }
      }

      const method = existing && confirmOverride ? "PUT" : "POST";
      const url = method === "PUT" ? `/views/${existing.id}` : "/views";

      const response = await apiClient[method.toLowerCase()](url, {
        type,
        name: finalViewName,
        filter: currentFilters,
        is_default: isDefault,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const savedView = await response.json();
      await fetchViews();

      setSelectedView(savedView);
      if (onApplyFilters && savedView.filter) {
        onApplyFilters(savedView.filter);
      }

      setShowModal(false);
      setConfirmOverride(false);
      setViewName("");
    } catch (err) {
      console.error("Error saving view:", err);
      if (allowLocalFallback) {
        try {
          saveLocalView(finalViewName);
        } catch (storageError) {
          setStorageNotice(storageError.message || "Could not save this view.");
        }
      } else {
        setStorageNotice(err.message || "Could not save this view.");
      }
    }
  };

  const handleSaveEdit = useCallback(
    async (viewId) => {
      if (!editingViewName.trim()) return;
      try {
        const viewToUpdate = views.find((v) => v.id === viewId);
        if (viewId.startsWith("local:")) {
          writeLocalViews(readLocalViews().map((view) => view.id === viewId ? { ...view, name: editingViewName } : view));
        } else {
          const response = await apiClient.put(`/views/${viewId}`, { ...viewToUpdate, name: editingViewName });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
        }
        await fetchViews();
        setEditingViewId(null);
        setEditingViewName("");
      } catch (err) {
        console.error("Error updating view:", err);
        const view = views.find((item) => item.id === viewId);
        if (allowLocalFallback && view) {
          const localCopy = { ...view, id: `local:${Date.now()}`, name: editingViewName };
          writeLocalViews([...readLocalViews().filter((item) => item.name !== view.name), localCopy]);
          setStorageNotice("Updated on this browser. Server storage is unavailable.");
          setEditingViewId(null);
          setEditingViewName("");
        } else {
          setStorageNotice(err.message || "Could not update this view.");
        }
      }
    },
    [editingViewName, views]
  );

  const handleCancelEdit = useCallback(() => {
    setEditingViewId(null);
    setEditingViewName("");
  }, []);

  const openDeleteModal = useCallback((view) => {
    setViewToDelete(view);
    setShowDeleteModal(true);
  }, []);

  const handleConfirmDelete = async () => {
    if (!viewToDelete) return;
    try {
      if (viewToDelete.id.startsWith("local:")) {
        writeLocalViews(readLocalViews().filter((view) => view.id !== viewToDelete.id));
      } else {
        const response = await apiClient.delete(`/views/${viewToDelete.id}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
      }
      await fetchViews();
      if (selectedView?.id === viewToDelete.id) {
        setSelectedView(null);
        if (onClearFilters) onClearFilters();
      }
      setShowDeleteModal(false);
      setViewToDelete(null);
    } catch (err) {
      console.error("Error deleting view:", err);
      if (allowLocalFallback) {
        writeLocalViews([...readLocalViews().filter((view) => view.name !== viewToDelete.name), { ...viewToDelete, id: `local:${Date.now()}`, deleted: true }]);
        setStorageNotice("Deleted from this browser. Server storage is unavailable.");
        setShowDeleteModal(false);
        setViewToDelete(null);
        if (selectedView?.name === viewToDelete.name) {
          setSelectedView(null);
          if (onClearFilters) onClearFilters();
        }
      } else {
        setStorageNotice(err.message || "Could not delete this view.");
      }
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setViewToDelete(null);
  };

  const dropdownOptions = useMemo(() =>
    views.map((v) => {
      if (editingViewId === v.id) {
        return {
          label: (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "4px 0",
              }}
            >
              <input
                type="text"
                value={editingViewName}
                onChange={(e) => setEditingViewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveEdit(v.id);
                  if (e.key === "Escape") handleCancelEdit();
                }}
                style={{
                  background: "#333",
                  border: "1px solid #555",
                  color: "white",
                  padding: "4px 8px",
                  borderRadius: 4,
                  flex: 1,
                }}
                autoFocus
              />
              <button
                onClick={() => handleSaveEdit(v.id)}
                style={{
                  background: "var(--ms-accent, #3fa9f5)",
                  border: "none",
                  borderRadius: 4,
                  padding: "4px 8px",
                  cursor: "pointer",
                  color: "white",
                }}
              >
                ✓
              </button>
              <button
                onClick={handleCancelEdit}
                style={{
                  background: "#666",
                  border: "none",
                  borderRadius: 4,
                  padding: "4px 8px",
                  cursor: "pointer",
                  color: "white",
                }}
              >
                ✕
              </button>
            </div>
          ),
          value: v.id,
          isDisabled: true,
        };
      }
      return {
        label: (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <span>{v.name}</span>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleDefault(v.id);
                }}
                style={{
                  cursor: "pointer",
                  fontSize: 16,
                  color: v.is_default ? "#ffd700" : "#666",
                }}
                title={v.is_default ? "Remove as default" : "Set as default"}
              >
                {v.is_default ? "★" : "☆"}
              </span>
              {!v.is_default && (
                <>
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      openDeleteModal(v);
                    }}
                    style={{
                      cursor: "pointer",
                      color: "#ff4444",
                      fontSize: 14,
                    }}
                    title="Delete view"
                  >
                    🗑️
                  </span>
                </>
              )}
            </div>
          </div>
        ),
        value: v.id,
      };
    })
  );

  return (
    <div ref={compactRef} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <style>{`
        input[type="checkbox"]#setDefault {
          appearance: none;
          -webkit-appearance: none;
          width: 18px;
          height: 18px;
          border: 2px solid #555;
          border-radius: 3px;
          cursor: pointer;
          position: relative;
          background-color: transparent;
        }
        input[type="checkbox"]#setDefault:checked {
          background-color: var(--ms-accent, #3fa9f5);
          border-color: var(--ms-accent, #3fa9f5);
        }
        input[type="checkbox"]#setDefault:checked::after {
          content: "✓";
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: #000000;
          font-size: 14px;
          font-weight: bold;
        }
      `}</style>

      <div
  style={{
    display: "grid",
    gridTemplateColumns: compact ? "30px" : "36px 1fr",
    gap: 8,
    alignItems: "center",
    width: compact ? "auto" : "100%",
    position: "relative",
  }}
>
  {compact && (
    <button type="button" className={`ms-header-icon${savedOpen ? " is-active" : ""}`} aria-label="Saved Views" aria-expanded={savedOpen} data-tooltip="Saved Views" onClick={() => setSavedOpen((value) => !value)}>
      <Star size={16} fill="currentColor" aria-hidden="true" />
    </button>
  )}
  {!compact && <button
    type="button"
    onClick={openSaveModal}
    title="Save current view"
    aria-label="Save current view"
    className={compact ? "ms-header-icon" : undefined}
    data-tooltip={compact ? "Save current view" : undefined}
    style={{
      height: compact ? 30 : 36,
      width: compact ? 30 : 36,
      borderRadius: 6,
      border: "1px solid #444",
      background: "#111",
      color: compact ? "#fff" : "#bbb",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 16,
      lineHeight: 1,
    }}
  >
    <Save size={16} aria-hidden="true" />
  </button>}

  {compact && savedOpen && (
    <div aria-label="Saved Views menu" style={{ position: "absolute", top: 40, left: 0, width: 280, maxWidth: "calc(calc(var(--mg-vw) * 100) - 40px)", padding: 8, border: "1px solid #384149", borderRadius: 8, background: "#080b0e", zIndex: 10050 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 4px 8px", color: "#cfd8e3", fontSize: 13 }}>
        <span>Saved Views</span>
        <button type="button" className="ms-header-icon" aria-label="Save current view" data-tooltip="Save current view" onClick={() => { setSavedOpen(false); openSaveModal(); }} style={{ color: "#fff" }}>
          <Save size={16} aria-hidden="true" />
        </button>
      </div>
      <div style={{ maxHeight: "min(400px, calc(var(--mg-vh) * 60))", overflowY: "auto" }}>
        {storageNotice && <div role="status" style={{ padding: "8px 8px 10px", color: "#9aa6b2", fontSize: 11, lineHeight: 1.4 }}>{storageNotice}</div>}
        {loading ? <div style={{ padding: 10, color: "#9aa6b2" }}>Loading views...</div> : !dropdownOptions.length ? <div style={{ padding: 10, color: "#9aa6b2", fontSize: 12 }}>No saved views</div> : dropdownOptions.map((option) => (
          <div key={option.value} role="button" tabIndex={option.isDisabled ? -1 : 0} aria-label={`Apply ${views.find((view) => view.id === option.value)?.name || "saved view"}`} onClick={() => { if (!option.isDisabled) { handleSelectView(option); setSavedOpen(false); } }} onKeyDown={(event) => { if (!option.isDisabled && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); handleSelectView(option); setSavedOpen(false); } }} style={{ padding: "10px 8px", borderTop: "1px solid #252b30", color: "#d4dce4", fontSize: 13, cursor: option.isDisabled ? "default" : "pointer", background: selectedView?.id === option.value ? "rgba(var(--ms-accent-rgb, 63,169,245),0.12)" : "transparent" }}>
            {option.label}
          </div>
        ))}
      </div>
    </div>
  )}
  {!compact && <div style={{ minWidth: 0 }}>
  <VirtualizedDropdown
    ref={dropdownRef}
    placeholder={loading ? "..." : "Saved Views"}
    options={dropdownOptions}
    value={
      selectedView
        ? { label: selectedView.name, value: selectedView.id }
        : null
    }
    onChange={handleSelectView}
    isClearable
    menuWidth="100%"
    isLoading={loading}
  />
  </div>}
</div>


      {showModal &&
        createPortal(
          <div
            className="mg-saved-view-modal"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "calc(var(--mg-vw) * 100)",
              height: "calc(var(--mg-vh) * 100)",
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 30000,
            }}
          >
            <div
              style={{
                width: "450px",
                background: "#1e1e1e",
                color: "#fff",
                borderRadius: 8,
                padding: 30,
              }}
            >
              {!confirmOverride ? (
                <>
                  <h3 style={{ marginBottom: 10 }}>Save current view</h3>
                  <p>Choose an existing view to update or create a new one</p>
                  <TextInputField
                    placeholder="View name"
                    value={viewName}
                    onChange={(e) => setViewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSave();
                    }}
                  />

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginTop: 20,
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <input
                        type="checkbox"
                        id="setDefault"
                        checked={isDefault}
                        onChange={(e) => setIsDefault(e.target.checked)}
                      />
                      <label
                        htmlFor="setDefault"
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        Set as default
                      </label>
                    </div>

                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        onClick={() => {
                          setShowModal(false);
                          setViewName("");
                        }}
                        style={{
                          background: "transparent",
                          border: "solid 1px",
                          padding: "8px 16px",
                          color: "#aaa",
                          cursor: "pointer",
                          borderRadius: "6px",
                          fontSize: "14px",
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSave}
                        style={{
                          background: "var(--ms-accent, #3fa9f5)",
                          border: "none",
                          padding: "8px 16px",
                          color: "#000000",
                          fontWeight: "600",
                          cursor: "pointer",
                          borderRadius: "6px",
                          fontSize: "14px",
                        }}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <p>
                      A view with this name already exists. Do you want to
                      override it?
                    </p>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginTop: 30,
                      }}
                    >
                      <button
                        onClick={() => setConfirmOverride(false)}
                        style={{
                          background: "transparent",
                          border: "solid 1px",
                          padding: "8px 16px",
                          color: "#aaa",
                          cursor: "pointer",
                          borderRadius: "6px",
                          fontSize: "14px",
                        }}
                      >
                        No
                      </button>
                      <button
                        onClick={handleSave}
                        style={{
                          background: "var(--ms-accent, #3fa9f5)",
                          border: "none",
                          padding: "8px 16px",
                          color: "#000000",
                          fontWeight: "600",
                          cursor: "pointer",
                          borderRadius: "6px",
                          fontSize: "14px",
                        }}
                      >
                        Yes
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>,
          document.body
        )}
      {showDeleteModal &&
        createPortal(
          <div
            className="mg-saved-view-modal"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "calc(var(--mg-vw) * 100)",
              height: "calc(var(--mg-vh) * 100)",
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10000,
            }}
          >
            <div
              style={{
                width: "30rem",
                background: "#1e1e1e",
                color: "#fff",
                borderRadius: 8,
                padding: 20,
              }}
            >
              <h3 style={{ marginBottom: 10 }}>Delete view</h3>
              <p>
                Are you sure you want to delete the view "{viewToDelete?.name}"?
              </p>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                  marginTop: 20,
                }}
              >
                <button
                  onClick={handleCancelDelete}
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: "8px 16px",
                    color: "#aaa",
                    cursor: "pointer",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  style={{
                    background: "#ef4444",
                    border: "none",
                    padding: "8px 16px",
                    color: "#fff",
                    fontWeight: "600",
                    cursor: "pointer",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
